// The app frame for signed-in pages (revamp PR 3): sidebar rail, top bar and the page area.
// It owns the frame state and hands it to the sidebar and top bar through render props, so the
// dashboard and the reviewer area can share it with their own navigation.
// Notices above the page come in through `banners`: the dashboard passes its impersonation and trial
// banners. The frame mounts none itself, because those banners call the main-user API, and inside the
// reviewer portal (its own session, no main-user token) that call's 401 redirects to /auth.
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/lumen/sheet';
import { useSidebar } from '@/contexts/sidebar-context';
import { useLanguage } from '@/contexts/LanguageContext';

export interface FrameState {
  /** Desktop rail collapsed to 64px. Persisted under `jarvis-sidebar-minimized` (kept from the old shell). */
  collapsed: boolean;
  toggleCollapsed: () => void;
  /** Off-canvas drawer below 992px. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
  /** At least 992px wide: the rail is permanent and ⌘B collapses it. */
  isDesktop: boolean;
}

const SIDEBAR_MINIMIZED_KEY = 'jarvis-sidebar-minimized';
const DESKTOP_QUERY = '(min-width: 992px)';

function readMinimized() {
  try {
    return localStorage.getItem(SIDEBAR_MINIMIZED_KEY) === 'true';
  } catch {
    return false;
  }
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setIsDesktop(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

export function AppFrame({
  sidebar,
  topbar,
  banners,
  children,
}: {
  sidebar: (state: FrameState) => ReactNode;
  topbar: (state: FrameState) => ReactNode;
  /** Notices above the page (the dashboard's impersonation and trial banners). */
  banners?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(readMinimized);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Pages hide the rail or the header for full-viewport screens (src/contexts/sidebar-context.tsx).
  const { isVisible, isHeaderVisible } = useSidebar();

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_MINIMIZED_KEY, String(next));
      } catch {
        // Blocked storage: the toggle still works for this visit.
      }
      return next;
    });
  }, []);

  // Leaving the mobile layout closes the drawer, so it never reopens on its own.
  useEffect(() => {
    if (isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  // Navigating closes the drawer, whatever started it (a rail link, the ⌘K palette, a page button).
  const { pathname, search } = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname, search]);

  // ⌘B / Ctrl+B collapses the rail on desktop.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'b') return;
      if (!isDesktop || !isVisible) return;
      event.preventDefault();
      toggleCollapsed();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDesktop, isVisible, toggleCollapsed]);

  const state: FrameState = {
    collapsed: isDesktop && collapsed,
    toggleCollapsed,
    mobileOpen,
    setMobileOpen,
    isDesktop,
  };

  return (
    <div className="flex min-h-screen bg-canvas text-fg">
      {isVisible && isDesktop && (
        // rem, not px: the rail follows the text-size setting like the rest of the app.
        <div
          data-slot="app-frame-rail"
          className="sticky top-0 h-screen shrink-0 overflow-hidden overscroll-contain transition-[width] duration-300 ease-in-out motion-reduce:transition-none"
          style={{ width: state.collapsed ? '4rem' : '15rem' }}
        >
          {sidebar(state)}
        </div>
      )}
      {isVisible && !isDesktop && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-[min(17.5rem,85vw)] p-0" showCloseButton={false}>
            <SheetTitle className="sr-only">{t('lumen.shell.menu')}</SheetTitle>
            <SheetDescription className="sr-only">{t('lumen.shell.menuDescription')}</SheetDescription>
            {sidebar(state)}
          </SheetContent>
        </Sheet>
      )}
      <div data-slot="app-frame-main" className="flex min-w-0 flex-1 flex-col">
        {/* Full-viewport pages (the review screen) switch the bar off and start at the very top. */}
        {isHeaderVisible && topbar(state)}
        {/* The old AppMain's padding (Mantine py="lg" px="xs"): old pages carry their own inner padding
            and were laid out against it. New pages add theirs with <PageShell>. */}
        <main data-slot="app-frame-page" className="min-w-0 flex-1 px-2.5 py-5">
          {banners}
          {children}
        </main>
      </div>
    </div>
  );
}
